import React from "react";
import "./BootcampTraderAdvantage.css";

import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  CheckCircle2,
  ClipboardCheck,
  LineChart,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";

import chartBg from "../../images/Chart 2.webp";
import chartAccent from "../../images/Chart 5.webp";

const supportItems = [
  {
    id: "trade-plan",
    icon: ClipboardCheck,
    title: "Technical trade plan",
    body: "Turn chart ideas into clear entries, stops, targets and invalidation.",
  },
  {
    id: "risk-control",
    icon: ShieldCheck,
    title: "Risk-first discipline",
    body: "Size positions, set limits and protect capital before chasing reward.",
  },
  {
    id: "live-support",
    icon: Users,
    title: "Live trade support",
    body: "Trade live market sessions with professional feedback and accountability.",
  },
  {
    id: "mentor-pathway",
    icon: Target,
    title: "Mentor-led pathway",
    body: "Build a part-time routine or prepare for a full-time trading path.",
  },
];

const reasonItems = [
  {
    id: "market-reading",
    icon: LineChart,
    title: "Read the market yourself",
    body: "Learn structure, levels and price behaviour instead of relying on tips.",
  },
  {
    id: "financial-optionality",
    icon: TrendingUp,
    title: "Build financial optionality",
    body: "Use trading as a skill-based side pursuit or a serious long-term pathway.",
  },
  {
    id: "funded-ready",
    icon: BadgeCheck,
    title: "Funded-account ready",
    body: "Finish with a tested playbook, review routine and challenge-ready plan.",
  },
];

const marketCandles = [
  "green",
  "green",
  "red",
  "green",
  "red",
  "green",
  "green",
  "red",
  "green",
  "green",
  "red",
  "green",
];

const supportTrends = ["gold", "green", "red", "green"];
const reasonTrends = ["gold", "green", "red"];

const getSupportTrend = (index) => supportTrends[index] || "green";
const getReasonTrend = (index) => reasonTrends[index] || "green";

function SupportCard({ item, index }) {
  const Icon = item.icon;

  return (
    <article
      className="trpc-traderAdvantage__supportCard"
      data-trend={getSupportTrend(index)}
    >
      <span
        className="trpc-traderAdvantage__supportStep"
        aria-hidden="true"
      >
        {String(index + 1).padStart(2, "0")}
      </span>

      <span
        className="trpc-traderAdvantage__supportCandle"
        aria-hidden="true"
      />

      <span className="trpc-traderAdvantage__iconWrap" aria-hidden="true">
        <Icon size={24} />
      </span>

      <div className="trpc-traderAdvantage__supportCopy">
        <h3>{item.title}</h3>
        <p>{item.body}</p>
      </div>
    </article>
  );
}

function ReasonCard({ item, index }) {
  const Icon = item.icon;

  return (
    <article
      className="trpc-traderAdvantage__reasonCard"
      data-trend={getReasonTrend(index)}
    >
      <span className="trpc-traderAdvantage__reasonShape" aria-hidden="true" />

      <span className="trpc-traderAdvantage__reasonIndex" aria-hidden="true">
        {String(index + 1).padStart(2, "0")}
      </span>

      <span className="trpc-traderAdvantage__reasonIcon" aria-hidden="true">
        <Icon size={28} />
      </span>

      <div className="trpc-traderAdvantage__reasonCopy">
        <span className="trpc-traderAdvantage__check" aria-hidden="true">
          <CheckCircle2 size={18} />
        </span>

        <h3>{item.title}</h3>
        <p>{item.body}</p>
      </div>
    </article>
  );
}

function BootcampTraderAdvantage({
  applyHref = "https://andus.co.za/trade-register",
}) {
  const applyLinkProps = applyHref.startsWith("http")
    ? {
        target: "_blank",
        rel: "noopener noreferrer",
      }
    : {};

  return (
    <section
      className="trpc-traderAdvantage"
      aria-labelledby="trpc-traderAdvantage-title"
    >
      <div className="trpc-traderAdvantage__shell">
        <div className="trpc-traderAdvantage__supportPanel">
          <img
            className="trpc-traderAdvantage__supportChart"
            src={chartBg}
            alt=""
            decoding="async"
            loading="lazy"
            aria-hidden="true"
          />

          <div
            className="trpc-traderAdvantage__supportShade"
            aria-hidden="true"
          />

          <div
            className="trpc-traderAdvantage__marketLayer"
            aria-hidden="true"
          >
            <span className="trpc-traderAdvantage__priceLine" />

            <div className="trpc-traderAdvantage__candleGroup">
              {marketCandles.map((trend, index) => (
                <span
                  key={`${trend}-${index}`}
                  className="trpc-traderAdvantage__candle"
                  data-trend={trend}
                />
              ))}
            </div>
          </div>

          <div className="trpc-traderAdvantage__header">
            <span className="trpc-traderAdvantage__kicker">
              <Sparkles size={16} aria-hidden="true" />
              Trader Advantage
            </span>

            <h2
              id="trpc-traderAdvantage-title"
              className="trpc-traderAdvantage__title"
            >
              Built to take your trading further
            </h2>

            <p className="trpc-traderAdvantage__subtitle">
              Practical execution, live-market support and the risk-first habits
              serious traders rely on.
            </p>
          </div>

          <div
            className="trpc-traderAdvantage__supportGrid"
            aria-label="Trading support outcomes"
          >
            {supportItems.map((item, index) => (
              <SupportCard item={item} index={index} key={item.id} />
            ))}
          </div>
        </div>

        <div className="trpc-traderAdvantage__reasonPanel">
          <img
            className="trpc-traderAdvantage__reasonChart"
            src={chartAccent}
            alt=""
            decoding="async"
            loading="lazy"
            aria-hidden="true"
          />

          <div
            className="trpc-traderAdvantage__reasonShade"
            aria-hidden="true"
          />

          <div
            className="trpc-traderAdvantage__reasonMarketLayer"
            aria-hidden="true"
          >
            <span className="trpc-traderAdvantage__reasonPriceLine" />

            <div className="trpc-traderAdvantage__reasonTicker">
              {marketCandles.slice(0, 8).map((trend, index) => (
                <span
                  key={`reason-${trend}-${index}`}
                  className="trpc-traderAdvantage__miniCandle"
                  data-trend={trend}
                />
              ))}
            </div>
          </div>

          <div className="trpc-traderAdvantage__reasonHeader">
            <span className="trpc-traderAdvantage__reasonKicker">
              <BookOpen size={16} aria-hidden="true" />
              Why Price Action?
            </span>

            <h2 className="trpc-traderAdvantage__reasonTitle">
              Learn the part of trading you can control
            </h2>

            <p className="trpc-traderAdvantage__reasonSubtitle">
              Price action gives you a practical market-reading process:
              analyse, plan, execute, review.
            </p>
          </div>

          <div
            className="trpc-traderAdvantage__reasonGrid"
            aria-label="Reasons to choose price action trading"
          >
            {reasonItems.map((item, index) => (
              <ReasonCard item={item} index={index} key={item.id} />
            ))}
          </div>

          <div className="trpc-traderAdvantage__bottomBar">
            <div className="trpc-traderAdvantage__truthNote">
              <span>Reality-first training</span>
              <strong>
                Trading involves risk. The goal is not hype — it is process,
                discipline and evidence.
              </strong>
            </div>

            <a
              className="trpc-traderAdvantage__cta"
              href={applyHref}
              data-candle="green"
              {...applyLinkProps}
            >
              <span>Apply now</span>
              <ArrowRight size={18} aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default BootcampTraderAdvantage;